import React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classnames from 'classnames';
import { InputMode } from '../../lib/types';
import { FormLabel } from '../FormLabel/FormLabel';
import RedactedInput from '../RedactedInput/RedactedInput';
import FormError from '../FormError/FormError';
import CharacterCount from '../CharacterCount/CharacterCount';

export type InputProps = {
	label?: string;
	type: string;
	placeholder?: string | undefined;
	icon?: IconProp;
	success?: boolean;
	error?: string;
	extraClasses?: string;
	extraInputClasses?: string;
	white?: boolean;
	inputMode?: InputMode;
	required?: boolean;
	tooltip?: string;
	name: string;
	disabled?: boolean;
	min?: number;
	max?: number;
	redacted?: boolean;
	prefix?: string;
	ariaLabel?: string;
	tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
	value: string | null;
	onChange: (value: string | null) => void;
	nullable?: boolean;
	maxCharacters?: number;
	minCharacters?: number;
	autoFocus?: boolean;
	step?: number;
	autoComplete?: boolean;
};

export function Input({
	label,
	onChange,
	type,
	placeholder,
	error,
	white = false,
	value,
	inputMode = 'text',
	required = false,
	tooltip,
	extraClasses = '',
	extraInputClasses = '',
	name,
	disabled = false,
	nullable = false,
	min = 0,
	max,
	redacted = false,
	// prefix = '',
	ariaLabel,
	tooltipPosition,
	maxCharacters,
	minCharacters,
	autoFocus = false,
	step = 1,
	autoComplete = false,
}: InputProps): JSX.Element {
	if (redacted) {
		return (
			<RedactedInput
				name={name}
				required={required}
				label={label}
				tooltip={tooltip}
				white={white}
			/>
		);
	}

	if (value === null) value = '';

	//add prefix in
	return (
		<div className={classnames('flex flex-col w-full', extraClasses)}>
			<div
				className={classnames(
					'flex flex-col w-full border-0 bg-pink-900 rounded-lg relative',
					{
						'opacity-50 text-slate-300 pointer-events-none': disabled,
						'bg-white': white,
						'bg-pink-900': !white,
					},
				)}
			>
				<FormLabel
					name={name}
					label={label}
					tooltip={tooltip}
					required={required}
					tooltipPosition={tooltipPosition}
				/>
				<input
					className={classnames(
						'placeholder-slate-200 font-light w-full px-2 pt-6 pb-2 border-0 text-base p-2 m-0 rounded-lg focus:ring-lightBlue-500 leading-6',
						{ 'bg-white': white, 'bg-pink-900': !white },
						extraInputClasses,
					)}
					onChange={event => {
						if (
							maxCharacters !== undefined &&
							event.target.value.length > maxCharacters
						) {
							return;
						}

						if (nullable === false) {
							return onChange(event.target.value);
						}

						if (event.target.value === '') {
							return onChange((null as unknown) as string);
						}

						onChange(event.target.value);
					}}
					autoFocus={autoFocus}
					type={type}
					placeholder={placeholder}
					value={value ?? ''}
					aria-label={ariaLabel || label}
					inputMode={inputMode}
					name={name}
					disabled={disabled}
					min={min}
					max={max}
					step={step}
					autoComplete={String(autoComplete)}
				/>
				{maxCharacters !== undefined && maxCharacters > 0 && (
					<CharacterCount
						text={value}
						max={maxCharacters}
						min={minCharacters}
					/>
				)}
			</div>
			{error && <FormError error={error} />}
		</div>
	);
}
